import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  AcceptAdminLegalPolicyInput,
  AdminLegalAcceptance,
  AdminIdentityPort,
  AdminIdentityCurrentUser,
  AdminIdentitySessionInfo,
  AdminIdentityUser,
  AdminPermissionGrant,
  AdminProfile,
  AdminUserRole,
} from "@tihany/module-admin-contracts";

export const createAdminIdentitySupabasePort = (
  supabase: SupabaseClient,
): AdminIdentityPort => ({
  async getSessionInfo() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    return {
      user: session?.user
        ? ({
            id: session.user.id,
            email: session.user.email ?? null,
          } as AdminIdentityCurrentUser)
        : null,
      expiresAt: session?.expires_at ?? null,
      errorMessage: error?.message ?? null,
    } as AdminIdentitySessionInfo;
  },
  async refreshSessionInfo() {
    const { data, error } = await supabase.auth.refreshSession();
    return {
      user: data.session?.user
        ? ({
            id: data.session.user.id,
            email: data.session.user.email ?? null,
          } as AdminIdentityCurrentUser)
        : null,
      expiresAt: data.session?.expires_at ?? null,
      errorMessage: error?.message ?? null,
    } as AdminIdentitySessionInfo;
  },
  onAuthStateChange(callback) {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      callback(
        session?.user
          ? {
              id: session.user.id,
              email: session.user.email ?? null,
            }
          : null,
      );
    });

    return {
      unsubscribe: () => {
        subscription.unsubscribe();
      },
    };
  },
  async getSessionUser() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) throw error;
    return session?.user ? ({ id: session.user.id } as AdminIdentityUser) : null;
  },
  async getCurrentUser() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error || !user) return null;
    return {
      id: user.id,
      email: user.email ?? null,
    } as AdminIdentityCurrentUser;
  },
  async getCurrentUserId() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error || !user) return null;
    return user.id;
  },
  async getSessionToken() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) return null;
    const token = session?.access_token?.trim();
    return token ? token : null;
  },
  async refreshSessionToken() {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) return null;
    const token = data.session?.access_token?.trim();
    return token ? token : null;
  },
  async invokeEdgeFunctionWithToken<T>(functionName: string, body: Record<string, unknown>, token: string) {
    const normalizedToken = token.trim();
    return supabase.functions.invoke(functionName, {
      body,
      headers: {
        Authorization: `Bearer ${normalizedToken}`,
      },
    }) as Promise<{ data: T | null; error: unknown }>;
  },
  async signInWithPassword(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  },
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },
  async isUserAdmin(userId: string) {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();

    return !error && Boolean(data);
  },
  async resetPasswordForEmail(email: string, redirectTo: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  },
  async updateProfileFullName(userId: string, fullName: string) {
    const { error } = await supabase.from("profiles").update({ full_name: fullName }).eq("id", userId);
    if (error) throw error;
  },
  async getProfileById(userId: string) {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, email")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw error;
    return (data as AdminProfile | null) ?? null;
  },
  async listProfiles() {
    const { data, error } = await supabase.from("profiles").select("id, full_name, email").order("full_name");
    if (error) throw error;
    return (data ?? []) as AdminProfile[];
  },
  async listUserRoles(userIds?: string[]) {
    let query = supabase.from("user_roles").select("user_id, role");
    if (userIds && userIds.length > 0) {
      query = query.in("user_id", userIds);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as AdminUserRole[];
  },
  async listPermissionGrants(userIds?: string[]) {
    let query = supabase.from("user_permission_grants").select("user_id, permission_key, granted");
    if (userIds && userIds.length > 0) {
      query = query.in("user_id", userIds);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as AdminPermissionGrant[];
  },
  async getLegalAcceptance(policyKey: string, policyVersion: string) {
    const normalizedPolicyKey = policyKey.trim();
    const normalizedPolicyVersion = policyVersion.trim();
    if (!normalizedPolicyKey || !normalizedPolicyVersion) return null;

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) return null;

    const { data, error } = await supabase
      .from("user_legal_acceptances")
      .select("user_id, policy_key, policy_version, locale, accepted_at")
      .eq("user_id", user.id)
      .eq("policy_key", normalizedPolicyKey)
      .eq("policy_version", normalizedPolicyVersion)
      .maybeSingle();

    if (error) throw error;
    return (data as AdminLegalAcceptance | null) ?? null;
  },
  async acceptLegalPolicy(input: AcceptAdminLegalPolicyInput) {
    const normalizedPolicyKey = input.policyKey.trim();
    const normalizedPolicyVersion = input.policyVersion.trim();
    const normalizedLocale = input.locale.trim().toLowerCase();

    if (!normalizedPolicyKey) {
      throw new Error("policyKey is required");
    }
    if (!normalizedPolicyVersion) {
      throw new Error("policyVersion is required");
    }
    if (!normalizedLocale) {
      throw new Error("locale is required");
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error("No active user");

    const { error: upsertError } = await supabase
      .from("user_legal_acceptances")
      .upsert(
        {
          user_id: user.id,
          policy_key: normalizedPolicyKey,
          policy_version: normalizedPolicyVersion,
          locale: normalizedLocale,
          accepted_at: new Date().toISOString(),
        },
        {
          onConflict: "user_id,policy_key,policy_version",
          ignoreDuplicates: true,
        },
      );

    if (upsertError) throw upsertError;

    const { data: acceptedRow, error: selectError } = await supabase
      .from("user_legal_acceptances")
      .select("user_id, policy_key, policy_version, locale, accepted_at")
      .eq("user_id", user.id)
      .eq("policy_key", normalizedPolicyKey)
      .eq("policy_version", normalizedPolicyVersion)
      .maybeSingle();

    if (selectError) throw selectError;
    if (!acceptedRow) {
      throw new Error("Legal acceptance write succeeded but record was not found");
    }

    return acceptedRow as AdminLegalAcceptance;
  },
});
